import { Box, Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type { EditActionDto } from "../../api/types";
import { SortableList, moveItem, useSortableIds } from "../SortableList";
import { ActionEditor } from "./ActionEditor";

export type ActionListEditorProps = {
  actions: EditActionDto[];
  onChange: (next: EditActionDto[]) => void;
  isMobile: boolean;
  /**
   * The names a bare `{Navn}` may refer to — the enclosing action set's inputs,
   * or `null` in a rule's own actions, where there are no inputs to name.
   */
  templateInputs?: readonly string[] | null;
};

/**
 * The actions a rule performs, in the order they are applied.
 *
 * One editor serves both places actions are written: an action set *is* the
 * action half of a rule, so the rule form stacks this under its condition
 * editor and the library form uses it on its own. Order is meaningful — a later
 * action overwrites what an earlier one wrote, and a `{$description}` template
 * reads whatever the actions above it left — so the rows can be dragged.
 */
export const ActionListEditor = ({
  actions,
  onChange,
  isMobile,
  templateInputs = null,
}: ActionListEditorProps) => {
  const ids = useSortableIds(actions.length);

  const handleMove = (from: number, to: number) => {
    ids.move(from, to);
    onChange(moveItem(actions, from, to));
  };

  const handleRemove = (index: number) => {
    ids.remove(index);
    onChange(actions.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    ids.append();
    onChange([...actions, { field: "description", value: "" }]);
  };

  return (
    <Stack spacing={2} sx={{ alignItems: "stretch" }}>
      <SortableList ids={ids.ids} itemLabel="action" onMove={handleMove}>
        {(index, handle) => (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "flex-start", width: "100%" }}
          >
            {/* Nudged down so the grip lines up with the first row of controls
                rather than the card's top border. */}
            <Box sx={{ pt: 0.5 }}>{handle}</Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <ActionEditor
                action={actions[index]}
                onChange={(next) =>
                  onChange(actions.map((a, i) => (i === index ? next : a)))
                }
                onRemove={() => handleRemove(index)}
                isMobile={isMobile}
                templateInputs={templateInputs}
              />
            </Box>
          </Stack>
        )}
      </SortableList>
      <Button
        startIcon={<AddIcon />}
        onClick={handleAdd}
        sx={{ alignSelf: "flex-start" }}
      >
        Add Action
      </Button>
    </Stack>
  );
};
